import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { useContext, useEffect, useState } from 'react';
import AppContext from 'app/AppContext';
import Order from 'communication/types/order/Order';
import {
    selectPortfolioSelectedOrders,
    selectStreamedOrSelectedOrders,
    useSelectActiveInput,
    useSelectPortfolioSelection,
} from './selectors';
import {
    PortfolioPosition,
    PortfolioSelection,
    PortfolioSymbolQuantity,
} from 'portfolio/PortfolioSelection';
import { useDispatch, useSelector } from 'react-redux';
import { selectStreamingOrders } from 'app/store/orders/selectors';
import { portfolioSetSelection } from 'portfolio/store/actions';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState.ts';

export const usePortfolioSelection: () => PortfolioSelectionWrapper = () => {
    const selectionState = useSelectPortfolioSelection();
    return new PortfolioSelectionWrapper(selectionState);
};

export interface UseFilteredPortfolioOptions {
    includeBlankSymbols: boolean;
    delayMs?: number;
}

export const usePortfolioSelectionSymbology: () => SymbologyValues = () => {
    const selection = usePortfolioSelection();
    return selection.symbology;
};

export const usePortfolioSelectedOrder: () => Order | undefined = () => {
    const selection = usePortfolioSelection();
    return selection.selectedOrder;
};

export const usePortfolioSelectedOrders: () => Order[] = () => {
    const selection = usePortfolioSelection();
    return selection.selectedOrders;
};

export const usePortfolioSelectedSymbol: () => string | undefined = () => {
    const selection = usePortfolioSelection();
    return selection.selectedSymbol;
};

export const usePortfolioSelectedSymbols: () => string[] = () => {
    const selection = usePortfolioSelection();
    return selection.selectedSymbols;
};

export const usePortfolioSelectedPosition: () =>
    | PortfolioPosition
    | undefined = () => {
    const selection = usePortfolioSelection();
    return selection.selectedPosition;
};

export const usePortfolioSelectedSymbolQuantity: () =>
    | PortfolioSymbolQuantity
    | undefined = () => {
    const selection = usePortfolioSelection();
    return selection.selectedSymbolQuantity;
};

export const usePortfolioSelectedSymbolQuantities: () => PortfolioSymbolQuantity[] =
    () => {
        const selection = usePortfolioSelection();
        return selection.selectedSymbolQuantities;
    };

export const usePortfolioActiveInputUsesOrdersStream: () => boolean = () => {
    const [result, setResult] = useState(false);

    const { services } = useContext(AppContext);
    const activeInput = useSelectActiveInput();

    useEffect(() => {
        setResult(services.portfolioService.activeInputUsesOrdersStream);
    }, [services.portfolioService.activeInputUsesOrdersStream, activeInput]);

    return result;
};

/*
 * Selects the orders streamed from Newport, or from the Portfolio Selection (i.e. selected in the Blotter in Standalone)
 * Returns undefined if the streamed or selected orders do not exist or are empty
 */
export const useSelectPortfolioStreamedOrSelectedOrders = () => {
    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();
    const selectedOrders = useSelector(selectPortfolioSelectedOrders);
    const streamingOrders = useSelector(selectStreamingOrders);

    return selectStreamedOrSelectedOrders(
        streamingOrders,
        selectedOrders,
        activeInputUsesOrdersStream
    );
};

/*
 * Returns the single position from the streamed order ids in Newport, or the selection in Standalone
 * This only returns a result if the selection originated from orders
 */
export const usePortfolioStreamedOrSelectedPositionFromOrders = () => {
    const streamedOrSelectedOrders =
        useSelectPortfolioStreamedOrSelectedOrders();
    const selectedPosition = usePortfolioSelectedPosition();

    if (!selectedPosition) {
        // The streamed or selected orders must make a position...
        return undefined;
    }

    if (!streamedOrSelectedOrders) {
        // We must have the streaming orders...
        return undefined;
    }

    return selectedPosition;
};

export const usePortfolioStreamedOrSelectedSymbolQuantityFromOrders = () => {
    const streamedOrSelectedOrders =
        useSelectPortfolioStreamedOrSelectedOrders();
    const selectedSymbolQuantity = usePortfolioSelectedSymbolQuantity();

    if (!selectedSymbolQuantity) {
        // The streamed or selected orders must make a symbol quantity...
        return undefined;
    }

    if (!streamedOrSelectedOrders) {
        // We must have the streaming orders...
        return undefined;
    }

    return selectedSymbolQuantity;
};

/*
 * Returns the single symbol from the streamed order ids in Newport, or the selection in Standalone
 * This only returns a result if the selection originated from orders
 */
export const usePortfolioStreamedOrSelectedSymbolFromOrders = () => {
    const streamedOrSelectedOrders =
        useSelectPortfolioStreamedOrSelectedOrders();
    const selectedSymbol = usePortfolioSelectedSymbol();

    if (!selectedSymbol) {
        // The streamed or selected orders must make a position...
        return undefined;
    }

    if (!streamedOrSelectedOrders) {
        // We must have the streaming orders...
        return undefined;
    }

    return selectedSymbol;
};

export const useDispatchSetPortfolioSelection = () => {
    const dispatch = useDispatch();
    return (selection: PortfolioSelection) => {
        dispatch(portfolioSetSelection(selection));
    };
};
